import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import ErrorReporter from '@app/errorReporter';
import { DownOutlined } from '@ant-design/icons';
import { Table, Button } from '@brokerbay/design-components';
import { ColumnsType } from 'antd/lib/table';
import { Dropdown, MenuProps, message } from 'antd';
import {
  getUserDocumentList,
  getDocumentByDocumentId,
  sendSupraDocumentsEmail,
} from 'api/SupraController';
import { featureEnabled } from 'utils';
import type { UserDocumentListData } from '@/app-package';
import {
  StyledCard as DefaultStyledCard,
  StyledDateCell,
  StyledNoContent,
  StyledNoContentWrapper,
  StyledSubtitle,
} from '../InvoicesCard';

export type DocumentCardProps = {
  userBillingNumber?: string;
  /** Optional custom card component for styling variations */
  CardComponent?: React.ComponentType<any>;
};

type UserDocumentListDataWithURL = UserDocumentListData & { url: string };

async function getEncodedStringfromApi(
  documentId: number,
): Promise<string | null> {
  try {
    const response = await getDocumentByDocumentId({ documentId });
    if (!response || !response.documentEncodedString) {
      message.error('Document not found');
      ErrorReporter.error('Document not found');
      return null;
    }
    return response.documentEncodedString;
  } catch (err) {
    message.error('Error fetching document');
    ErrorReporter.error('Error fetching document', err);
    return null;
  }
}

const handleViewClick = async (documentId: number): Promise<void> => {
  const response = await getEncodedStringfromApi(documentId);
  if (response) {
    openPdf(response);
  }
};

function openPdf(enCodedString: string) {
  try {
    const binaryData = atob(enCodedString);
    const byteArray = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      byteArray[i] = binaryData.charCodeAt(i);
    }
    const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
  } catch (err) {
    message.error('Error opening PDF');
    ErrorReporter.error('Error opening PDF', err);
  }
}

const DocumentsCard: React.FC<DocumentCardProps> = ({
  userBillingNumber,
  CardComponent = DefaultStyledCard,
}) => {
  const [dataSource, setDataSource] = useState<
    UserDocumentListData[] | UserDocumentListDataWithURL[]
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const getActionMenu = (id: number): MenuProps => ({
    items: [
      {
        key: 'view',
        label: 'View / Print',
        onClick: () => handleViewClick(id),
        style: { padding: '0.5em 4em 0.5em 1em' },
      },
      {
        key: 'email',
        label: 'Email Copy',
        onClick: () => handleEmailClick(id),
        style: { padding: '0.5em 4em 0.5em 1em' },
      },
    ],
  });

  const handleEmailClick = async (documentId: number): Promise<void> => {
    if (!dataSource.length || dataSource.length < 1) return;

    const doc = dataSource.find(
      (item: UserDocumentListData) => item.id === documentId,
    );

    if (!doc) return;

    const { id, title } = doc;

    try {
      await sendSupraDocumentsEmail({
        documents: [{ documentId: id.toString(), documentName: title }],
      });

      message.success('Your email was sent.');
    } catch (e) {
      message.error(
        'There was a problem sending the email. If this problem persists, please contact support.',
      );
    }
  };

  const columns: ColumnsType<UserDocumentListData> = [
    {
      title: 'Type',
      key: 'title',
      dataIndex: 'title',
      width: 130,
      render: (title: string) => `${title}`,
    },
    {
      title: 'Action',
      key: 'actionType',
      dataIndex: 'actionType',
      width: 130,
      sorter: (a, b) => a.actionType.localeCompare(b.actionType),
      render: (actionType: string) => `${actionType}`,
    },
    {
      title: 'Date',
      key: 'createdDate',
      dataIndex: 'createdDate',
      width: 130,
      render: (createdDate: Date) => (
        <StyledDateCell>
          {moment(createdDate).format('MM/DD/YYYY')}
        </StyledDateCell>
      ),
    },
    {
      key: 'id',
      dataIndex: 'id',
      width: 120,
      render: id => (
        <Dropdown menu={getActionMenu(id)} trigger={['click']}>
          <Button type="link">
            Action <DownOutlined style={{ color: '#0056d2' }} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    if (featureEnabled('keyholderBillingFeature')) {
      const fetchData = async () => {
        setLoading(true);
        if (userBillingNumber) {
          const docs = await getUserDocumentList({ userBillingNumber });

          const docsWithUrls = docs.map(doc => ({ ...doc, url: '' }));

          setDataSource(docsWithUrls);
          setTotalCount(docsWithUrls.length);
        }
        setLoading(false);
      };
      fetchData();
    }
  }, [userBillingNumber]);

  const dynamicColumns = useMemo(() => {
    const uniqueTypes = Array.from(new Set(dataSource.map(doc => doc.title)));
    return columns.map(col => {
      if ('dataIndex' in col && col.dataIndex === 'title') {
        return {
          ...col,
          filters: uniqueTypes.map(type => ({ text: type, value: type })),
          onFilter: (value: string, record: UserDocumentListData) =>
            record.title === value,
        };
      }
      return col;
    });
  }, [dataSource]);

  return (
    <CardComponent
      title="Documents"
      subtitle={
        Boolean(totalCount) && (
          <StyledSubtitle type="secondary">
            {totalCount} {totalCount === 1 ? 'Result' : 'Results'}
          </StyledSubtitle>
        )
      }
      withContent={Boolean(totalCount)}
    >
      <Table
        dataSource={dataSource}
        rowKey="id"
        columns={dynamicColumns}
        pagination={false}
        loading={loading}
        scroll={{ x: 'max-content', y: 385 }}
        locale={{
          emptyText: <StyledNoContent>No documents found</StyledNoContent>,
        }}
      />
    </CardComponent>
  );
};

export default DocumentsCard;
